import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';

import {
  getBenefitCostByCategory,
  getBenefitCostByDepartment,
  getBenefitEnrollment,
  getBenefitDependentDemographics,
  getBenefitCafeteriaUtilization,
  getBenefitApplicationSla,
  getBenefitLifeEventThroughput,
  getBenefitPiiExport,
  exportBenefitReport,
} from '../../services/benefitReportService';
import Button from '../../components/ui/Button';
import Tabs from '../../components/ui/Tabs';
import DataTable from '../../components/ui/DataTable';
import { parseTableParams, formatDate } from '../../utils/Utils';

const toPascal = (v) => (v || '').split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');

const Container = styled.div``;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $s }) => {
    if ($s === 'active' || $s === 'approved' || $s === 'activated' || $s === 'resolved') return '#D1FAE5';
    if ($s === 'submitted' || $s === 'waiting' || $s === 'reminding' || $s === 'acknowledged') return '#FEF3C7';
    if ($s === 'rejected' || $s === 'expired' || $s === 'terminated') return '#FEE2E2';
    if ($s === 'draft' || $s === 'pending') return '#E0E7FF';
    if ($s === 'closed' || $s === 'locked') return '#E5E7EB';
    return '#F1F5F9';
  }};
  color: ${({ $s }) => {
    if ($s === 'active' || $s === 'approved' || $s === 'activated' || $s === 'resolved') return '#065F46';
    if ($s === 'submitted' || $s === 'waiting' || $s === 'reminding' || $s === 'acknowledged') return '#92400E';
    if ($s === 'rejected' || $s === 'expired' || $s === 'terminated') return '#991B1B';
    if ($s === 'draft' || $s === 'pending') return '#3730A3';
    if ($s === 'closed' || $s === 'locked') return '#475569';
    return '#475569';
  }};
`;

const PiiWarning = styled.div`
  background: #FEF3C7;
  border-left: 4px solid #D97706;
  padding: 12px 16px;
  margin-bottom: 16px;
  border-radius: 4px;
  color: #78350F;
  font-size: 13px;
`;

const formatCurrency = (v) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v || 0);

const useReportConfigs = (t, lang) => useMemo(() => [
  {
    slug: 'cost-by-category',
    label: t('benefitReports.costByCategory'),
    fetch: getBenefitCostByCategory,
    columns: [
      { name: 'categoryName', label: t('benefitCategory.name'), options: { filter: false, sort: true } },
      { name: 'benefitCount', label: t('benefitReports.benefitCount'), options: { filter: false, sort: true } },
      { name: 'employeeCount', label: t('benefitReports.employeeCount'), options: { filter: false, sort: true } },
      { name: 'monthlyCompanyCost', label: t('benefitReports.companyCost'), options: { filter: false, sort: true, customBodyRender: (v) => formatCurrency(v) } },
      { name: 'monthlyEmployeeCost', label: t('benefitReports.employeeCost'), options: { filter: false, sort: false, customBodyRender: (v) => formatCurrency(v) } },
    ],
  },
  {
    slug: 'cost-by-department',
    label: t('benefitReports.costByDepartment'),
    fetch: getBenefitCostByDepartment,
    columns: [
      { name: 'departmentName', label: t('employee.department'), options: { filter: false, sort: true } },
      { name: 'employeeCount', label: t('benefitReports.employeeCount'), options: { filter: false, sort: true } },
      { name: 'assignmentCount', label: t('benefitReports.assignmentCount'), options: { filter: false, sort: true } },
      { name: 'monthlyCompanyCost', label: t('benefitReports.companyCost'), options: { filter: false, sort: true, customBodyRender: (v) => formatCurrency(v) } },
    ],
  },
  {
    slug: 'enrollment',
    label: t('benefitReports.enrollment'),
    fetch: getBenefitEnrollment,
    columns: [
      { name: 'benefitName', label: t('benefit.name'), options: { filter: false, sort: true } },
      { name: 'categoryName', label: t('benefitCategory.title'), options: { filter: false, sort: false } },
      { name: 'activeCount', label: t('benefitReports.activeCount'), options: { filter: false, sort: true, customBodyRender: (v) => <StatusBadge $s="active">{v}</StatusBadge> } },
      { name: 'pendingCount', label: t('benefitReports.pendingCount'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s="pending">{v}</StatusBadge> } },
      { name: 'terminatedCount', label: t('benefitReports.terminatedCount'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s="terminated">{v}</StatusBadge> } },
      { name: 'monthlyCompanyCost', label: t('benefitReports.companyCost'), options: { filter: false, sort: true, customBodyRender: (v) => formatCurrency(v) } },
    ],
  },
  {
    slug: 'dependent-demographics',
    label: t('benefitReports.dependentDemographics'),
    fetch: getBenefitDependentDemographics,
    columns: [
      { name: 'type', label: t('benefitDependent.type'), options: { filter: false, sort: true, customBodyRender: (v) => t(`benefitDependent.type${toPascal(v)}`, v) } },
      { name: 'activeCount', label: t('benefitReports.activeCount'), options: { filter: false, sort: true } },
      { name: 'pendingRemovalCount', label: t('benefitReports.pendingRemovalCount'), options: { filter: false, sort: false } },
      { name: 'averageAge', label: t('benefitReports.averageAge'), options: { filter: false, sort: true } },
      { name: 'minorCount', label: t('benefitReports.minorCount'), options: { filter: false, sort: false } },
      { name: 'adultCount', label: t('benefitReports.adultCount'), options: { filter: false, sort: false } },
    ],
  },
  {
    slug: 'cafeteria-utilization',
    label: t('benefitReports.cafeteriaUtilization'),
    fetch: getBenefitCafeteriaUtilization,
    columns: [
      { name: 'periodName', label: t('openEnrollment.title'), options: { filter: false, sort: true } },
      { name: 'periodStatus', label: t('openEnrollment.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{t(`openEnrollment.status${toPascal(v)}`, v)}</StatusBadge> } },
      { name: 'eligibleEmployees', label: t('benefitReports.eligibleEmployees'), options: { filter: false, sort: false } },
      { name: 'submittedSelections', label: t('benefitReports.submittedSelections'), options: { filter: false, sort: true } },
      { name: 'lockedSelections', label: t('benefitReports.lockedSelections'), options: { filter: false, sort: false } },
      { name: 'activatedSelections', label: t('benefitReports.activatedSelections'), options: { filter: false, sort: false } },
      { name: 'totalAllocated', label: t('benefitReports.totalAllocated'), options: { filter: false, sort: false, customBodyRender: (v) => formatCurrency(v) } },
      { name: 'averageAllocation', label: t('benefitReports.averageAllocation'), options: { filter: false, sort: false, customBodyRender: (v) => formatCurrency(v) } },
    ],
  },
  {
    slug: 'application-sla',
    label: t('benefitReports.applicationSla'),
    fetch: getBenefitApplicationSla,
    columns: [
      { name: 'employeeName', label: t('employee.title'), options: { filter: false, sort: true } },
      { name: 'benefitName', label: t('benefit.name'), options: { filter: false, sort: false } },
      { name: 'status', label: t('benefitApplication.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{t(`benefitApplication.status${toPascal(v)}`, v)}</StatusBadge> } },
      { name: 'createDate', label: t('common.createDate'), options: { filter: false, sort: true, customBodyRender: (v) => formatDate(v, lang) } },
      { name: 'slaDeadline', label: t('benefitReports.slaDeadline'), options: { filter: false, sort: false, customBodyRender: (v) => v ? formatDate(v, lang) : '—' } },
      { name: 'daysOpen', label: t('benefitReports.daysOpen'), options: { filter: false, sort: true, customBodyRender: (v) => v != null ? v : '—' } },
      { name: 'breached', label: t('benefitReports.breached'), options: { filter: false, sort: false, customBodyRender: (v) => v ? <StatusBadge $s="rejected">{t('common.yes')}</StatusBadge> : <StatusBadge $s="active">{t('common.no')}</StatusBadge> } },
    ],
  },
  {
    slug: 'life-event-throughput',
    label: t('benefitReports.lifeEventThroughput'),
    fetch: getBenefitLifeEventThroughput,
    columns: [
      { name: 'eventType', label: t('lifeEvent.type'), options: { filter: false, sort: true, customBodyRender: (v) => t(`lifeEvent.type${toPascal(v)}`, v) } },
      { name: 'submitted', label: t('lifeEvent.statusSubmitted'), options: { filter: false, sort: true } },
      { name: 'acknowledged', label: t('lifeEvent.statusAcknowledged'), options: { filter: false, sort: false } },
      { name: 'resolved', label: t('lifeEvent.statusResolved'), options: { filter: false, sort: false } },
      { name: 'expired', label: t('lifeEvent.statusExpired'), options: { filter: false, sort: false } },
      { name: 'averageResolutionDays', label: t('benefitReports.averageResolutionDays'), options: { filter: false, sort: true } },
    ],
  },
  {
    slug: 'pii-export',
    label: t('benefitReports.piiExport'),
    isSensitive: true,
    fetch: getBenefitPiiExport,
    columns: [
      { name: 'employeeName', label: t('employee.title'), options: { filter: false, sort: true } },
      { name: 'dependentType', label: t('benefitDependent.type'), options: { filter: false, sort: false, customBodyRender: (v) => t(`benefitDependent.type${toPascal(v)}`, v) } },
      { name: 'dependentName', label: t('benefitDependent.name'), options: { filter: false, sort: false } },
      { name: 'citizenId', label: t('benefitDependent.citizenId'), options: { filter: false, sort: false, customBodyRender: (v) => v || '—' } },
      { name: 'birthDate', label: t('benefitDependent.birthDate'), options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) } },
      { name: 'status', label: t('common.status'), options: { filter: false, sort: false, customBodyRender: (v) => <StatusBadge $s={v}>{v}</StatusBadge> } },
    ],
  },
], [t, lang]);


function ReportTable({ report }) {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 50, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageSize: 50, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (params) => {
    setFetching(true);
    try {
      const res = await report.fetch(params);
      const result = res.data?.data;
      const items = result?.data || [];
      setData(items);
      setPagination({
        totalRowCount: result?.totalRowCount || 0,
        pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
        pageSize: result?.pageSize || 50,
      });
    } finally {
      setFetching(false);
    }
  }, [report]);

  React.useEffect(() => { fetchData(queryParams); }, [queryParams, fetchData]);

  const handleDataStateChange = useCallback((_, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, report.columns);
    setQueryParams(params);
  }, [report]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await exportBenefitReport(report.slug, { ...queryParams, pageSize: 10000, pageNumber: 1 });
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const match = res.headers['content-disposition']?.match(/filename[^;=\n]*=([^;\n]*)/);
      a.download = match ? match[1].replaceAll('"', '') : `${report.slug}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [queryParams, report.slug]);

  return (
    <div>
      {report.isSensitive && (
        <PiiWarning>{t('benefitReports.piiWarning')}</PiiWarning>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button type="button" $variant="outline" onClick={handleExport} disabled={exporting || data.length === 0}>
          <Download size={14} style={{ marginRight: 6 }} />
          {exporting ? '...' : t('benefitReports.exportExcel')}
        </Button>
      </div>
      <DataTable
        title={report.label}
        fetching={fetching}
        columns={report.columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        disableFilters
      />
    </div>
  );
}


export default function BenefitReportsPage() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const reports = useReportConfigs(t, i18n.language);

  const initialTab = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const tab = qs.get('tab');
    return reports.find(r => r.slug === tab)?.slug || reports[0].slug;
  }, [location.search, reports]);

  const [activeTab, setActiveTab] = useState(initialTab);

  const tabs = reports.map(r => ({ key: r.slug, label: r.label }));
  const active = reports.find(r => r.slug === activeTab) || reports[0];

  return (
    <Container>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      <div style={{ marginTop: 16 }}>
        <ReportTable key={active.slug} report={active} />
      </div>
    </Container>
  );
}
